import { PowerBiDashboard } from "@/interfaces";
import { useQuery } from "@tanstack/react-query";
import api from "./api";

export type PowerBiEmbedInfo =
  | {
      objectType: "report";
      reportId: string;
      reportName: string;
      embedUrl: string;
      embedToken: string;
      tokenExpiration: string;
      datasetId?: string;
    }
  | {
      objectType: "app";
      appId: string;
      appName: string;
      embedUrl: string;
      embedToken: string;
      tokenExpiration: string;
    };

export const useGetEmbedInfo = (id: string) => {
  return useQuery<PowerBiEmbedInfo>({
    queryKey: ["powerbi-embed-info", id],
    queryFn: async () => {
      const response = await api.get(`/powerbi/embed-info/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useGetDashboardById = (id: string) => {
  return useQuery<PowerBiDashboard>({
    queryKey: ["powerbi-dashboard", id],
    queryFn: async () => {
      const response = await api.get(`/powerbi/view/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
};

export const useGetAllDashboards = () => {
  return useQuery<PowerBiDashboard[]>({
    queryKey: ["powerbi-dashboards", "all"],
    queryFn: async () => {
      const response = await api.get("/powerbi/all");
      return response.data.data;
    },
  });
};

export const useGetDashboards = (companyId: string) => {
  return useQuery<PowerBiDashboard[]>({
    queryKey: ["powerbi-dashboards", companyId],
    queryFn: async () => {
      const response = await api.get(`/powerbi/${companyId}`);
      return response.data.data;
    },
    enabled: !!companyId,
  });
};

export const useGetUserDashboards = (userId: string, companyId: string) => {
  return useQuery<PowerBiDashboard[]>({
    queryKey: ["powerbi-user-dashboards", userId, companyId],
    queryFn: async () => {
      // companyId may be blank (e.g. a PowerBiUser with no company assigned). The backend's
      // getUserDashboards still correctly returns company-agnostic dashboards (no companies
      // attached) regardless of what's passed here -- it only uses this param to further filter
      // company-scoped ones. A placeholder keeps the URL well-formed without changing that result.
      const response = await api.get(`/powerbi/user/${userId}/${companyId || "none"}`);
      return response.data.data;
    },
    enabled: !!userId,
  });
};

interface PowerBiDashboardFormData {
  name: string;
  description?: string;
  objectType?: "report" | "app";
  dashboardUrl?: string;
  workspaceId?: string;
  appId?: string;
  companyIds: string[];
  userIds: string[];
  useRLS?: boolean;
  roles?: string[];
}

export const powerBiService = {
  createDashboard: async (data: PowerBiDashboardFormData) => {
    const response = await api.post("/powerbi", data);
    return response.data;
  },
  updateDashboard: async (id: string, data: PowerBiDashboardFormData) => {
    const response = await api.put(`/powerbi/${id}`, data);
    return response.data;
  },
  deleteDashboard: async (id: string) => {
    const response = await api.delete(`/powerbi/${id}`);
    return response.data;
  },
};
